import {
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  NoSymbolIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

import {
  StatusChip,
  StatusChipProps,
  StatusChipVariant,
} from '@/components/ui/status-chip';
import { Risk, RiskStatus } from '@/types';
import { cn } from '@/utils/classname';
import { getRiskStatusLabel } from '@/utils/riskStatus.util';
import {
  getVariantForStatus,
  vulnerabilityStatusVariants,
} from '@/utils/statusChip.util';
import { getStatusLabel } from '@/utils/vulnerabilityStatus.util';

export function VulnerabilityStatusComponent(props: {
  vulnerability: Risk;
  className?: string;
}) {
  const { vulnerability, className } = props;

  const vulnerabilityStatusProps = getVulnerabilityStatusProps(vulnerability);

  if (!vulnerabilityStatusProps) return null;

  return <StatusChip {...vulnerabilityStatusProps} className={cn(className)} />;
}

interface VulnerabilityStatusChipProps extends StatusChipProps {
  variant: StatusChipVariant;
}

// eslint-disable-next-line react-refresh/only-export-components
export const getVulnerabilityStatusProps = (
  vulnerability: Risk
): VulnerabilityStatusChipProps | null => {
  if (!vulnerability?.status) return null;

  const { status, statusLabel } = getRiskStatusLabel(vulnerability.status);

  switch (status) {
    case RiskStatus.Triaged:
      return {
        label: getStatusLabel(RiskStatus.Triaged),
        variant: getVariantForStatus('triaged', vulnerabilityStatusVariants),
        icon: <ClockIcon className="size-3 shrink-0" />,
        tooltip: 'Vulnerability is detected and under analysis',
      };

    case RiskStatus.Opened:
      return {
        label: getStatusLabel(RiskStatus.Opened),
        variant: getVariantForStatus('opened', vulnerabilityStatusVariants),
        icon: <ExclamationTriangleIcon className="size-3 shrink-0" />,
        tooltip: 'Vulnerability is demonstrated and requires action',
      };

    case RiskStatus.Remediated:
      return {
        label: getStatusLabel(RiskStatus.Remediated),
        variant: getVariantForStatus('remediated', vulnerabilityStatusVariants),
        icon: <CheckCircleIcon className="size-3 shrink-0" />,
        tooltip: 'Vulnerability has been resolved',
      };

    case RiskStatus.Accepted:
      return {
        label: getStatusLabel(RiskStatus.Accepted),
        variant: getVariantForStatus('accepted', vulnerabilityStatusVariants),
        icon: <NoSymbolIcon className="size-3 shrink-0" />,
        tooltip: 'Vulnerability risk has been accepted',
      };

    case RiskStatus.Deleted:
      return {
        label: getStatusLabel(RiskStatus.Deleted),
        variant: getVariantForStatus('deleted', vulnerabilityStatusVariants),
        icon: <XCircleIcon className="size-3 shrink-0" />,
        tooltip: 'Vulnerability has been rejected',
      };

    default:
      return {
        label: statusLabel || 'Unknown',
        variant: 'muted',
        icon: null,
        tooltip: `Status: ${statusLabel || 'Unknown'}`,
      };
  }
};
